import { type RefObject, useEffect, useState } from 'react';

/**
 * True while any part of `ref` is in the viewport, or in the part of it
 * that `rootMargin` leaves (negative margins shrink the area watched).
 *
 * For pausing work nobody can see, such as a continuous animation. It
 * changes a few times per visit at most, so React state is the right home
 * for it.
 */
export const useInView = (ref: RefObject<Element | null>, rootMargin?: string): boolean => {
  const [inView, setInView] = useState(true);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setInView(entry.isIntersecting);
      },
      { rootMargin },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref, rootMargin]);

  return inView;
};
